import React, { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';
import { clearBranchContext, setBranchContext } from '../services/posthog';

interface BranchOverrideContextType {
  overrideUuid: string | null;
  overrideEmail: string | null;
  setOverride: (branchId: string | null, email?: string | null) => void;
  setOverrideUuid: (branchId: string | null) => void;
}

const BranchOverrideContext = createContext<BranchOverrideContextType>({
  overrideUuid: null,
  overrideEmail: null,
  setOverride: () => {},
  setOverrideUuid: () => {},
});

export const BranchOverrideProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [overrideUuid, setUuid] = useState<string | null>(
    sessionStorage.getItem('branchOverrideUuid')
  );
  const [overrideEmail, setEmail] = useState<string | null>(
    sessionStorage.getItem('branchOverrideEmail')
  );

  const setOverride = (branchId: string | null, email?: string | null) => {
    if (branchId) {
      sessionStorage.setItem('branchOverrideUuid', branchId);
      // Update PostHog to track impersonation
      setBranchContext(branchId, true).catch(err =>
        console.debug('[BranchOverride] Failed to update PostHog:', err)
      );
    } else {
      sessionStorage.removeItem('branchOverrideUuid');
      // Clear override tracking - UserRoleProvider will restore actual branch
      clearBranchContext();
    }
    if (email) {
      sessionStorage.setItem('branchOverrideEmail', email);
    } else {
      sessionStorage.removeItem('branchOverrideEmail');
    }
    setUuid(branchId);
    setEmail(email || null);
  };

  const setOverrideUuid = (branchId: string | null) => setOverride(branchId, null);

  return (
    <BranchOverrideContext.Provider value={{ overrideUuid, overrideEmail, setOverride, setOverrideUuid }}>
      {children}
    </BranchOverrideContext.Provider>
  );
};

export const useBranchOverride = () => useContext(BranchOverrideContext);
